import { assertOk, readJsonLines } from "../stream";
import type {
  ChatRequest,
  CompletionResult,
  LLMProvider,
  StreamChunk,
} from "../types";

export interface OllamaOptions {
  baseUrl: string;
  model: string;
  maxTokens: number;
  extraHeaders?: Record<string, string>;
}

/**
 * Ollama local runtime (http://localhost:11434). Text-only in this prototype —
 * tool support is intentionally off (`supportsTools = false`) so the controller
 * always uses the streaming path for local models.
 */
export class OllamaProvider implements LLMProvider {
  readonly kind = "ollama" as const;
  readonly supportsTools = false;

  constructor(private readonly opts: OllamaOptions) {}

  private endpoint(): string {
    return `${this.opts.baseUrl.replace(/\/$/, "")}/api/chat`;
  }

  private body(req: ChatRequest, stream: boolean): Record<string, unknown> {
    const messages: unknown[] = [];
    if (req.system) messages.push({ role: "system", content: req.system });
    for (const m of req.messages) {
      messages.push({ role: m.role, content: m.content });
    }
    return {
      model: req.model,
      stream,
      messages,
      options: { num_predict: req.maxTokens },
    };
  }

  async *stream(
    req: ChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<StreamChunk> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: { "content-type": "application/json", ...this.opts.extraHeaders },
        body: JSON.stringify(this.body(req, true)),
        signal,
      }),
    );

    for await (const line of readJsonLines(res)) {
      const evt = line as {
        message?: { content?: string };
        done?: boolean;
        done_reason?: string;
      };
      if (evt.message?.content) yield { type: "text", text: evt.message.content };
      if (evt.done) {
        yield { type: "done", finishReason: evt.done_reason };
        break;
      }
    }
  }

  async complete(
    req: ChatRequest,
    signal?: AbortSignal,
  ): Promise<CompletionResult> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: { "content-type": "application/json", ...this.opts.extraHeaders },
        body: JSON.stringify(this.body(req, false)),
        signal,
      }),
    );
    const json = (await res.json()) as {
      message?: { content?: string };
      done_reason?: string;
    };
    return {
      text: json.message?.content ?? "",
      toolCalls: [],
      finishReason: json.done_reason,
    };
  }
}
